import { useEffect, useState } from "react";
import { useParams, useSearchParams, Navigate } from "react-router-dom";
import { getApiClient } from "../lib/apiClient";
import {
  blankDefinition,
  useEditorStore,
} from "../features/designer/editorStore";
import { Designer } from "../features/designer/Designer";
import type { FlowDefinition } from "../types/flow";
import { ApiError } from "../types/api";

export function FlowEditorPage() {
  const { flowId } = useParams<{ flowId: string }>();
  const [search] = useSearchParams();
  const [loadError, setLoadError] = useState<string | null>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!flowId) return;
    const client = getApiClient();
    let cancelled = false;

    (async () => {
      try {
        const flow = await client.flows.get(flowId);
        const versions = await client.versions.list(flowId);
        const targetId = search.get("v");
        const picked =
          (targetId && versions.find((v) => v.id === targetId)) ||
          versions.find((v) => v.state === "published") ||
          versions[versions.length - 1];
        const def: FlowDefinition = picked?.definition ?? blankDefinition();
        const sidecar = await client.flowSidecar.get(flow.id);
        if (!cancelled) {
          useEditorStore
            .getState()
            .loadFlow(flow.id, flow.name, def, sidecar.positions);
          setReady(true);
        }
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError && err.code === "FLOW_NOT_FOUND") {
          setLoadError("not_found");
        } else {
          setLoadError(err instanceof Error ? err.message : "unknown error");
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [flowId, search]);

  useEffect(() => {
    return () => {
      useEditorStore.getState().reset();
    };
  }, []);

  // Persist positions whenever they change (debounced). Covers drag-stop,
  // add-node, delete-node, and the auto-arrange button.
  useEffect(() => {
    let timer: number | null = null;
    const unsubscribe = useEditorStore.subscribe((state, prev) => {
      if (state.positions === prev.positions) return;
      if (!state.flowId) return;
      const fid = state.flowId;
      const positions = state.positions;
      if (timer !== null) window.clearTimeout(timer);
      timer = window.setTimeout(() => {
        void getApiClient().flowSidecar.save(fid, { positions });
      }, 250);
    });
    return () => {
      if (timer !== null) window.clearTimeout(timer);
      unsubscribe();
    };
  }, []);

  if (loadError === "not_found") {
    return <Navigate to="/flows" replace />;
  }
  if (loadError) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="max-w-md text-center">
          <h2 className="text-lg font-semibold mb-2">Couldn't load this flow</h2>
          <p className="text-sm text-neutral-500 mb-4">{loadError}</p>
        </div>
      </div>
    );
  }
  if (!ready) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-neutral-500">Loading flow…</p>
      </div>
    );
  }

  return <Designer />;
}
